import { useState, useEffect } from 'react';
import { Server, Globe, AlertCircle } from 'lucide-react';

function SingleIndicator({ label, status, icon: Icon }) {
  const getStatusColor = () => {
    switch (status) {
      case 'healthy': return '#10b981'; // green
      case 'degraded': return '#f59e0b'; // yellow
      case 'offline': return '#ef4444'; // red
      case 'checking': return '#6b7280'; // gray
      default: return '#6b7280';
    }
  };

  const displayIcon = status === 'degraded' || status === 'offline'
    ? <AlertCircle size={14} />
    : <Icon size={14} style={{ opacity: status === 'checking' ? 0.5 : 1 }} />;

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.375rem',
      padding: '0.4rem 0.5rem',
      borderRadius: '0.375rem',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: getStatusColor(),
      fontSize: '0.75rem',
      fontWeight: '500'
    }}>
      {displayIcon}
      <span>{label}</span>
    </div>
  );
}

export function HealthIndicator() {
  const [portalStatus, setPortalStatus] = useState('checking');
  const [internetStatus, setInternetStatus] = useState('checking');

  useEffect(() => {
    const checkPortalHealth = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        // Remove /api suffix if present to access root /health endpoint
        const baseUrl = import.meta.env.VITE_API_URL.replace(/\/api$/, '');
        const response = await fetch(
          `${baseUrl}/health`,
          {
            method: 'GET',
            signal: controller.signal
          }
        );

        clearTimeout(timeoutId);

        if (response.ok) {
          setPortalStatus('healthy');
        } else {
          setPortalStatus('degraded');
        }
      } catch (error) {
        console.error('Portal health check failed:', error);
        setPortalStatus('offline');
      }
    };

    const checkInternetHealth = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        // Check Cloudflare DNS (1.1.1.1) via HTTPS
        const response = await fetch(
          'https://1.1.1.1/dns-query?name=cloudflare.com&type=A',
          {
            method: 'GET',
            headers: { 'Accept': 'application/dns-json' },
            signal: controller.signal
          }
        );

        clearTimeout(timeoutId);

        if (response.ok) {
          setInternetStatus('healthy');
        } else {
          setInternetStatus('degraded');
        }
      } catch (error) {
        console.error('Internet health check failed:', error);
        setInternetStatus('offline');
      }
    };

    // Initial checks
    checkPortalHealth();
    checkInternetHealth();

    // Poll every 60 seconds
    const portalInterval = setInterval(checkPortalHealth, 60000);
    const internetInterval = setInterval(checkInternetHealth, 60000);

    return () => {
      clearInterval(portalInterval);
      clearInterval(internetInterval);
    };
  }, []);

  return (
    <div style={{
      width: '100%',
      display: 'flex',
      gap: '0.5rem'
    }}>
      <SingleIndicator label="Portal" status={portalStatus} icon={Server} />
      <SingleIndicator label="Internet" status={internetStatus} icon={Globe} />
    </div>
  );
}
